import { db } from "../index";

export interface RequestedAudioFile {
  /** The derived incident id the slice was requested under: {anchorIncidentId}_{deviceId}. */
  incidentId: string;
  deviceId: string;
  displayName?: string;
  /** Where the slice lands in the triangula-scout-audio bucket. */
  objectPath: string;
}

export interface AudioComparison {
  /** The anchor incident the comparison fanned out from; also the document id. */
  incidentId: string;
  /** The anchor incident's device; its own detection audio is the reference. */
  deviceId: string;
  /** Anchor incident time in TUT micros. */
  tut: number;
  /** Seconds of audio before the incident tut. */
  pre: number;
  /** Total window length in seconds, including pre. */
  length: number;
  /** Server time the comparison was requested, for filtering out stale upload results. */
  createdMs?: number;
  /** The triangulation (gunshot) the anchor incident belonged to, when known. */
  triangulationId?: string;
  files: RequestedAudioFile[];
}

const toAudioComparison = (id: string, data: any): AudioComparison => ({
  incidentId: data.incidentId ? data.incidentId : id,
  deviceId: data.deviceId,
  tut: data.tut,
  pre: data.pre,
  length: data.length,
  createdMs: data.created ? data.created.toMillis() : undefined,
  triangulationId: data.triangulationId ? data.triangulationId : undefined,
  files: ((data.files ?? []) as any[]).map((file) => ({
    incidentId: file.incidentId,
    deviceId: file.deviceId,
    displayName: file.displayName ? file.displayName : undefined,
    objectPath: file.objectPath,
  })),
});

const collection = (organisationId: string) =>
  db.doc("commander").collection("organisations").doc(organisationId).collection("audioComparisons");

/**
 * Listen for the record of an audio comparison ("request audio from every scout
 * in the area" in commander). backoffice-api writes one document per anchor
 * incident with every file it requested; the per-device outcomes arrive
 * separately in uploadedIncidents (see uploadedIncidents.ts) under each file's
 * derived incident id.
 *
 * Returns the unsubscribe function.
 */
export const listenForAudioComparison = (
  organisationId: string,
  incidentId: string,
  callback: (comparison: AudioComparison) => void,
) => {
  return collection(organisationId)
    .doc(incidentId)
    .onSnapshot((snapshot) => {
      const data = snapshot.data();
      if (!data || !data.files) {
        return;
      }
      callback(toAudioComparison(snapshot.id, data));
    });
};

/**
 * The organisation's audio comparisons, newest first. The collection has no
 * expiry, so cap the read; 50 covers far more history than anyone scrolls.
 */
export const listAudioComparisons = (organisationId: string): Promise<AudioComparison[]> => {
  return collection(organisationId)
    .orderBy("created", "desc")
    .limit(50)
    .get()
    .then((snapshot) => snapshot.docs.map((doc) => toAudioComparison(doc.id, doc.data())));
};
